import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as dealTemplateActionCreators from '@makemydeal/dr-dash-store/actions/dealTemplateActionCreators';
import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';
import { groupTemplatesByInventoryTypeYMMT } from './utils';
import { Accordion, AccordionDetails, AccordionSummary } from '@interstate/components/AccordionGroup';
import { TemplateListTable } from './templateListTable/TemplateListTable';
import { Box } from '@interstate/components/Box';
import { Badge } from '@interstate/components/Badge';
import type { DataToRemoveTemplate, DataToUpdateTemplate, DealTemplateInventoryTypeExtended } from '@makemydeal/dr-dash-types';
import { Modal } from '@interstate/components/Modal';
import { Spinner } from '@interstate/components/Spinner';
import { TEST_IDS } from '../constants';

const inventoryTypeToAccordionTitleMap: Record<DealTemplateInventoryTypeExtended, string> = {
    New: 'New Vehicle',
    Used: 'Used Vehicle',
    CertifiedPreOwned: 'CPO Vehicle',
    All: 'All Vehicles'
};

type TemplateListAccordionProps = {
    expandedItem: DealTemplateInventoryTypeExtended | undefined;
    handleEditTemplate: (templateId: string) => void;
};

const TemplateListAccordion = ({ expandedItem, handleEditTemplate }: TemplateListAccordionProps) => {
    const dispatch = useDispatch();

    const dealTemplateList = useSelector(dealTemplateSelectors.getDealTemplateList) || [];
    const isLoading = useSelector(dealTemplateSelectors.getIsDealTemplateListLoading);

    const [templateToRemove, setTemplateToRemove] = useState<DataToUpdateTemplate | null>(null);
    const onRemoveTemplate = (template: DataToRemoveTemplate) => {
        dispatch(dealTemplateActionCreators.removeDealTemplate(template));
        setTemplateToRemove(null);
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '400px'
                }}
            >
                <Spinner />
            </Box>
        );
    }

    if (dealTemplateList.length === 0) return <>This section is empty—no saved templates have been added yet.</>;

    // Usage in your component
    const { templatesForNewCars, templatesForUsedCars, templatesForCpoCars, templatesForAllCars } =
        groupTemplatesByInventoryTypeYMMT(dealTemplateList);

    const groupedTemplates = [templatesForAllCars, templatesForNewCars, templatesForUsedCars, templatesForCpoCars].filter(
        (templateList) => templateList.length > 0
    );

    return (
        <>
            {groupedTemplates.map((templatesByInventoryType) => {
                const inventoryType = templatesByInventoryType[templatesByInventoryType.length - 1].inventoryType;
                return (
                    <Box key={inventoryType}>
                        <Accordion
                            defaultExpanded={inventoryType === expandedItem}
                            data-testid={TEST_IDS.ACCORDION_VEHICLE(inventoryType)}
                            id={TEST_IDS.ACCORDION_VEHICLE(inventoryType)}
                        >
                            <AccordionSummary chevronPosition="left">
                                <Box display="flex" justifyContent="space-between" width="100%">
                                    <Box data-testid={`accordion-title-${inventoryType}`}>
                                        {inventoryTypeToAccordionTitleMap[inventoryType]}
                                    </Box>
                                    <Badge>{templatesByInventoryType.length}</Badge>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TemplateListTable
                                    handleEditTemplate={handleEditTemplate}
                                    data={templatesByInventoryType}
                                    setTemplateToRemove={setTemplateToRemove}
                                />
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                );
            })}
            {Boolean(templateToRemove) && (
                <Modal
                    show
                    size="large"
                    header="Delete Template"
                    onHide={() => setTemplateToRemove(null)}
                    footer={{
                        primary: {
                            label: 'Delete',
                            action: () => onRemoveTemplate(templateToRemove as DataToRemoveTemplate),
                            'data-testid': TEST_IDS.CONFIRM_DELETE_TEMPLATE,
                            buttonStyle: 'primary'
                        },
                        options: [
                            {
                                label: 'Cancel',
                                action: () => setTemplateToRemove(null),
                                'data-testid': TEST_IDS.CANCEL_DELETE_TEMPLATE,
                                buttonStyle: 'tertiary'
                            }
                        ]
                    }}
                >
                    <>
                        Are you sure you want to delete the <strong>{(templateToRemove as DataToUpdateTemplate).name}</strong>{' '}
                        template?
                    </>
                </Modal>
            )}
        </>
    );
};

export default TemplateListAccordion;
